import { Component, HostListener, computed, effect, inject, signal } from '@angular/core';
import { Location as queryState } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { AssetsService } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { SearchFilterComponent } from '@lp-ui-lib';
import { BreadcrumbsComponent } from '@lp-ui-lib';
import { AssetNavButtonComponent } from '@lp-ui-lib';
import { NavButtonComponent } from '@lp-ui-lib';
import { GLOBAL_CONSTANT } from '@lp-ui-lib';
import { BUILDING_LAYERS } from '@lp-ui-lib';
import { App_Routes } from '@lp-ui-lib';
import { Icons } from '@lp-ui-lib';
import { I18n_keys } from '@lp-ui-lib';
import { NameSlicePipe } from '@lp-ui-lib';
import { CardComponent } from '../../../components/card/card';

@Component({
  selector: 'app-plant-overview-content',
  imports: [
    SearchFilterComponent,
    BreadcrumbsComponent,
    NzAvatarModule,
    NzAlertModule,
    NzIconModule,
    AssetNavButtonComponent,
    NavButtonComponent,
    NameSlicePipe,
    CardComponent,
  ],
  templateUrl: './plant-overview-content.html',
  styleUrls: ['./plant-overview-content.scss'],
})
export class PlantOverviewContentComponent {
  private GLOBAL_CONSTANT = GLOBAL_CONSTANT;
  // Inject services
  private assetsService = inject(AssetsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private ui = inject(UiService);
  private queryState = inject(queryState);
  public i18n = inject(I18nService);

  // Signals for reactive state
  plant = signal<any>(null);
  loading = signal(true);
  init = signal(false);
  emptyResults = signal(false);
  plantID = signal<number>(this.GLOBAL_CONSTANT.ZERO);
  mobfilterStatus = signal<any>(null);

  // Computed signals
  isMobile = computed(() => this.ui.IsMobile());
  isTablet = computed(() => this.ui.IsTablet());
  isDesktop = computed(() => this.ui.IsDesktop());

  buildings = computed(() => {
    const plant = this.plant();
    if (plant?.children?.[this.GLOBAL_CONSTANT.ZERO]?.isAsset) return [plant];
    // EM: Filter out empty buildings/areas (only show those with assets)
    return plant?.children?.filter((building: any) => building.Assets.length > 0) ?? [];
  });

  assetCount = computed(() => {
    const plant = this.plant();
    return plant ? plant.Assets.length : this.GLOBAL_CONSTANT.ZERO;
  });

  constructor() {
    // Effect to handle route changes
    this.ui.onNavBack(() => this.handleNavBack());
    effect(() => {
      const params = this.route.snapshot.params;
      this.loading.set(true);
      this.plantID.set(+params[this.GLOBAL_CONSTANT.ID]);
      this.mobfilterStatus.set((this.queryState.getState() as any)?.status);
      this.assetsService.resetLocationFilter();
      const result: any = this.assetsService.plantsState();
      if (!result) return;
      this.loading.set(false);
      let newPlant = result.plants
        .map((item: any) => item.find(this.plantID()))
        .find((item: any) => !!item);

      while (
        newPlant &&
        newPlant.Assets.length > 25 &&
        !newPlant.NextLayerIsAboveEquipment &&
        newPlant.children.length === this.GLOBAL_CONSTANT.ONE
      ) {
        newPlant = newPlant.children[this.GLOBAL_CONSTANT.ZERO];
      }

      this.plant.set(newPlant);
      this.emptyResults.set(!newPlant);

      if (newPlant) {
        const parent = this.getParentForBackNav(newPlant.parentID);
        const parentName =
          parent?.curLayer !== BUILDING_LAYERS.A && parent?.curLayer !== BUILDING_LAYERS.B
            ? parent.objectName
            : this.i18n.langString(I18n_keys.HOME);
        this.ui.setBackText(this.i18n.parse(parentName));
        this.assetsService.updateSelectionPreset([...newPlant.breadcrumbs, newPlant]);
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    // Trigger change detection manually if needed
    if (this.isMobile() !== this.ui.IsMobile()) {
      this.isMobile();
    }
  }

  sortCritFirst(buildings: any[]) {
    return buildings
      .sort((x, y) =>
        x.critical === y.critical
          ? x.warning === y.warning
            ? this.GLOBAL_CONSTANT.ZERO
            : x.warning
              ? -1
              : this.GLOBAL_CONSTANT.ONE
          : x.critical
            ? -1
            : this.GLOBAL_CONSTANT.ONE
      )
      .sort((x, y) => x.objectName.localeCompare(y.objectName));
  }

  toLowerItem(building: any) {
    this.router.navigate([App_Routes.PLANT, building.objectID]);
  }

  private handleNavBack(): void {
    const plant = this.plant();
    if (!plant) {
      this.router.navigate([App_Routes.HOME], { replaceUrl: true });
    }

    let parent = this.getParentForBackNav(plant.parentID);
    let nextParent = this.assetsService.findLocation(parent?.parentID);

    while (
      nextParent &&
      parent.children.filter((c: any) => c.Assets.length > this.GLOBAL_CONSTANT.ZERO).length ===
        this.GLOBAL_CONSTANT.ONE &&
      nextParent.curLayer !== BUILDING_LAYERS.A
    ) {
      parent = nextParent;
      nextParent = this.assetsService.findLocation(nextParent.parentID);
    }

    if (
      !!parent &&
      parent?.curLayer !== BUILDING_LAYERS.A &&
      parent?.curLayer !== BUILDING_LAYERS.B
    ) {
      this.router.navigate([App_Routes.PLANT, parent.objectID], {
        replaceUrl: true,
      });
    } else {
      this.router.navigate([App_Routes.HOME, App_Routes.LIST], {
        replaceUrl: true,
      });
    }
  }

  statusChecked(asset: any) {
    if (asset.critical) return Icons.STAT_EXPLANATION_TRIANGLE;
    if (asset.warning) return Icons.STAT_EXPLANATION_OCTAGON;
    return Icons.STAT_WORKING_FINE;
  }

  groupBy<T>(arr: T[], key: keyof T): T[][] {
    return Object.values(
      arr.reduce(
        (acc: any, item) => {
          const groupKey = item[key];
          acc[groupKey] = acc[groupKey] || [];
          acc[groupKey].push(item);
          return acc;
        },
        {} as Record<string, T[]>
      )
    );
  }

  assetsGroupBy(plant: any) {
    return this.groupBy<any>(plant.Assets, 'parentID');
  }

  getParentForBackNav(parentID: number): any {
    let parent: any = this.assetsService.findLocation(parentID);
    while (parent && !['a', 'b', 'c'].includes(parent.curLayer)) {
      if (
        parent.Assets.length > 25 &&
        !parent.IsAboveEquipment &&
        !parent.NextLayerIsAboveEquipment
      )
        break;
      parent = this.assetsService.findLocation(parent.parentID);
    }

    if (!parent) {
      throw new Error(`Location not found for parentID: ${parentID}`);
    }

    return parent;
  }

  onDisabledAsset(building: any) {
    this.router.navigate([App_Routes.DISABLE_ASSETS, building.objectID]);
  }
}
